import React, { useState, useEffect } from 'react';
import { Grid, Box, Card, CardContent, Typography, useTheme, Skeleton, Chip } from '@mui/material';
import {
  People as PeopleIcon,
  Restaurant as RecipeIcon,
  MenuBook as BookIcon,
  LocalOffer as TagIcon,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { PageHeader, StatCard } from '../components/common';
import { adminApi } from '../utils/api';
import { format, formatDistanceToNow } from 'date-fns';

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return 'Unknown';
  try {
    return format(new Date(dateStr), 'MMM d, yyyy');
  } catch {
    return 'Unknown';
  }
};

// Format relative time
const formatRelative = (dateStr) => {
  if (!dateStr) return 'Never';
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return 'Unknown';
  }
};

// Section header component
const SectionHeader = ({ children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Typography 
      sx={{ 
        fontFamily: "'Space Mono', monospace",
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
        mb: 2,
      }}
    >
      {children}
    </Typography>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const { data } = await adminApi.getDashboardStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  // Chart tooltip
  const ChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: isDark ? '#1a1a30' : '#fff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            borderRadius: 1,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            p: 1.5,
          }}
        >
          <Typography 
            sx={{ 
              fontFamily: "'Space Mono', monospace",
              fontSize: '0.68rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
              mb: 0.75,
            }}
          >
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
              <Box 
                sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: 0.5,
                  backgroundColor: entry.color,
                }} 
              />
              <Typography
                sx={{ 
                  fontFamily: "'Space Mono', monospace",
                  fontSize: '0.72rem',
                  color: theme.palette.text.primary,
                }}
              >
                {entry.name}: <strong>{entry.value}</strong>
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  // List item for rankings - styled with circular gradient accent
  const RankItem = ({ index, title, subtitle, color }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.25,
        px: 1,
        mx: -1,
        borderRadius: 2,
        transition: 'background-color 0.15s ease',
        '&:hover': {
          backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        },
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          backgroundColor: `${color}20`,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'DotGothic16', sans-serif",
          fontSize: '0.9rem',
          color: color,
          flexShrink: 0,
        }}
      >
        {index + 1}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: "'Space Mono', monospace",
            fontSize: '0.76rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.4,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        <Typography 
          sx={{ 
            fontFamily: "'Space Mono', monospace",
            fontSize: '0.66rem',
            color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
            lineHeight: 1.3,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );

  // Empty state
  const EmptyState = ({ text }) => (
    <Box sx={{ 
      background: isDark 
        ? 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.01) 100%)'
        : 'linear-gradient(135deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.01) 100%)',
      border: `1px dashed ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, 
      borderRadius: 2,
      p: 4, 
      textAlign: 'center',
    }}>
      <Typography sx={{ 
        fontFamily: "'Space Mono', monospace", 
        fontSize: '0.72rem', 
        color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
        letterSpacing: '0.05em',
      }}>
        {text}
      </Typography>
    </Box>
  );

  // Card wrapper with consistent styling
  const StyledCard = ({ children, ...props }) => (
    <Card
      sx={{
        height: '100%',
        ...props.sx,
      }}
      {...props}
    >
      {children}
    </Card>
  );

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle="Welcome back! Here's what's happening with Reciply."
      />

      {/* Stats Grid - 4 cards per row on large screens */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats?.totalUsers}
            icon={PeopleIcon}
            trend={stats?.usersTrend}
            trendLabel="vs last week"
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Recipes"
            value={stats?.totalRecipes}
            icon={RecipeIcon}
            trend={stats?.recipesTrend}
            trendLabel="vs last week"
            color="secondary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Books"
            value={stats?.totalBooks}
            icon={BookIcon}
            color="info"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Tags"
            value={stats?.totalTags}
            icon={TagIcon}
            color="warning"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Activity Chart + Most Saved */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <StyledCard>
            <CardContent sx={{ p: 2.5 }}>
              <SectionHeader>Activity Overview</SectionHeader>
              {loading ? (
                <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
              ) : (
                <>
                  <Box 
                    sx={{ 
                      borderRadius: 2,
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                      p: 1.5,
                      background: isDark 
                        ? 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.1) 100%)'
                        : 'linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.01) 100%)',
                    }}
                  >
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={stats?.recentActivity || []}>
                        <defs>
                          <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorRecipes" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={theme.palette.secondary.main} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={theme.palette.secondary.main} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="date"
                          axisLine={false}
                          tickLine={false}
                          tick={{ 
                            fill: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', 
                            fontFamily: "'Space Mono', monospace",
                            fontSize: 11,
                          }}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ 
                            fill: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                            fontFamily: "'Space Mono', monospace",
                            fontSize: 11,
                          }}
                          allowDecimals={false}
                        />
                        <Tooltip content={<ChartTooltip />} />
                        <Area
                          type="linear"
                          dataKey="users"
                          name="Users"
                          stroke={theme.palette.primary.main}
                          fill="url(#colorUsers)"
                          strokeWidth={2}
                          dot={{ fill: theme.palette.primary.main, r: 3 }}
                        />
                        <Area
                          type="linear"
                          dataKey="recipes"
                          name="Recipes"
                          stroke={theme.palette.secondary.main}
                          fill="url(#colorRecipes)"
                          strokeWidth={2}
                          dot={{ fill: theme.palette.secondary.main, r: 3 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                  {/* Chart legend */}
                  <Box sx={{ display: 'flex', gap: 2.5, mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 10, height: 10, backgroundColor: theme.palette.primary.main, borderRadius: 0.5 }} />
                      <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: '0.7rem', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                        Users
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 10, height: 10, backgroundColor: theme.palette.secondary.main, borderRadius: 0.5 }} />
                      <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: '0.7rem', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                        Recipes
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader>Most Saved Recipes</SectionHeader>
              {loading ? (
                <>{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={56} sx={{ mb: 0.5, borderRadius: 1.5 }} />)}</>
              ) : stats?.mostSavedRecipes?.length > 0 ? (
                <Box>
                  {stats.mostSavedRecipes.map((recipe, index) => (
                    <RankItem
                      key={recipe.id}
                      index={index}
                      title={recipe.title}
                      subtitle={`${recipe.saves} ${recipe.saves === 1 ? 'save' : 'saves'}`}
                      color={theme.palette.primary.main}
                    />
                  ))}
                </Box>
              ) : (
                <EmptyState text="No data yet" />
              )}
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Top Sources & Recent Users */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader>Top Sources</SectionHeader>
              {loading ? (
                <>{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={56} sx={{ mb: 0.5, borderRadius: 1.5 }} />)}</>
              ) : stats?.topSources?.length > 0 ? (
                <Box>
                  {stats.topSources.map((source, index) => (
                    <RankItem
                      key={source.id}
                      index={index}
                      title={source.name}
                      subtitle={`${source.recipeCount} ${source.recipeCount === 1 ? 'recipe' : 'recipes'}${source.author ? ` · ${source.author}` : ''}`}
                      color={theme.palette.secondary.main}
                    />
                  ))}
                </Box>
              ) : (
                <EmptyState text="No data yet" />
              )}
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader>Recent Users</SectionHeader>
              {loading ? (
                <>{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={60} sx={{ mb: 0.5, borderRadius: 1.5 }} />)}</>
              ) : stats?.recentUsers?.length > 0 ? (
                <Box>
                  {stats.recentUsers.map((user) => (
                    <Box
                      key={user.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1.5,
                        px: 1,
                        mx: -1,
                        borderRadius: 1.5,
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: theme.palette.primary.main,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: "'VT323', monospace",
                          fontSize: '1.1rem',
                          color: '#fff',
                          flexShrink: 0,
                        }}
                      >
                        {user.email?.[0]?.toUpperCase()}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography 
                            sx={{ 
                              fontFamily: "'Space Mono', monospace",
                              fontSize: '0.78rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              lineHeight: 1.4,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {user.email}
                          </Typography>
                          {user.provider && user.provider !== 'email' && (
                            <Chip
                              label={user.provider}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.6rem',
                                fontFamily: "'Space Mono', monospace",
                                background: `linear-gradient(135deg, ${theme.palette.info.main}25 0%, ${theme.palette.info.main}15 100%)`,
                                color: theme.palette.info.main,
                                border: `1px solid ${theme.palette.info.main}30`,
                                '& .MuiChip-label': {
                                  px: 1,
                                },
                              }}
                            />
                          )}
                        </Box>
                        <Typography 
                          sx={{ 
                            fontFamily: "'Space Mono', monospace",
                            fontSize: '0.68rem',
                            color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                            lineHeight: 1.3,
                          }}
                        >
                          Joined {formatDate(user.createdAt)}
                          {user.lastSignInAt && ` · ${formatRelative(user.lastSignInAt)}`}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <EmptyState text="No users yet" />
              )}
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
